<template>
  <v-data-table
    :headers="headers"
    :items="filteredInstructions"
    :search="search"
    :custom-filter="searchFilter"
    :sort-by="sortBy"
    :sort-desc="sortDesc"
    fixed-header
    :loading="loading"
    :footer-props="{
      'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
      'items-per-page-all-text': `${$t('employees.footer.all')}`,
    }"
    class="safety-elevation-3 table-striped"
  >
    <template v-slot:top>
      <v-row align="center" class="mx-4">
        <v-text-field
          class="flex-grow-1 pr-4 py-5"
          v-model="search"
          append-icon="search"
          :label="$t('search.title')"
          solo
          :background-color="
            $vuetify.theme.themes.light.primary | hexToRgb(0.2)
          "
          dense
          flat
          single-line
          hide-details
        />
        <v-col cols="auto">
          <v-select
            v-model="filteredGroups"
            :items="groups"
            item-text="name"
            item-value="id"
            dense
            single-line
            flat
            :background-color="
              $vuetify.theme.themes.light.primary | hexToRgb(0.2)
            "
            solo
            hide-details
            attach
            chips
            :label="$t('instructions.group.filter')"
            multiple
          />
        </v-col>

        <v-btn
          text
          color="primary"
          @click="
            () => {
              filter = !filter;
              filters = [];
            }
          "
          >{{ $t("instructions.filter.title") }}
          <v-icon right>{{ filter ? "expand_less" : "expand_more" }}</v-icon>
        </v-btn>
        <v-btn :text="!showActive" color="primary" @click="setShowActive(true)"
          >{{ $t("instructions.tabs.active") }}
          {{
            activeInstructionCount && showActiveCounts
              ? activeInstructionCount
              : ""
          }}
        </v-btn>
        <v-btn :text="showActive" color="primary" @click="setShowActive(false)"
          >{{ $t("instructions.tabs.inactive") }}
          {{
            inactiveInstructionCount && showActiveCounts
              ? inactiveInstructionCount
              : ""
          }}
        </v-btn>
      </v-row>
      <v-row v-if="filter" align="center" class="mx-4">
        <v-autocomplete
          class="flex-grow-1 mb-5"
          v-model="filters"
          :placeholder="
            `${$t('instructions.filter.title')} - ${$t(
              'instructions.filter.noTags'
            )}`
          "
          :items="tags"
          required
          chips
          solo
          deletable-chips
          hide-details
          :item-text="getDetailedTagText"
          multiple
          return-object
        />
      </v-row>
    </template>
    <template v-slot:[`item.code`]="{ item }">
      <span :class="getActiveStatusStyle(item)">{{ item.code }}</span>
    </template>

    <template v-slot:[`item.groups[0].name`]="{ item }">
      <v-chip small class="ml-1 mb-1" v-if="hasGroups(item)">
        {{ item.groups[0].name }}
      </v-chip>
    </template>

    <template
      v-if="canEdit"
      v-slot:[`item.version.localizedFile.name`]="{ item }"
    >
      <a
        :class="getActiveStatusStyle(item)"
        href="#"
        @click.prevent="$emit('edit', item.id)"
        >{{ item.version.localizedFile.name }}</a
      >
    </template>

    <template v-else v-slot:[`item.version.localizedFile.name`]="{ item }">
      <a
        :class="getActiveStatusStyle(item)"
        target="blank"
        :href="item.version.localizedFile.file.url"
        >{{ item.version.localizedFile.name }}</a
      >
    </template>

    <template v-slot:[`item.version.date`]="{ item }">
      <span :class="getActiveStatusStyle(item)">
        {{ item.version.revision }}
        ({{ item.version.date | date }})
      </span>
    </template>

    <template v-slot:[`item.version.dueInDays`]="{ item }">
      <span :class="getActiveStatusStyle(item)">{{
        item.version.dueInDays
      }}</span>
    </template>

    <template v-slot:[`item.version.rereadingInDays`]="{ item }">
      <span :class="getActiveStatusStyle(item)">{{
        item.version.rereadingInDays ? item.version.rereadingInDays : ""
      }}</span>
    </template>

    <template v-slot:[`item.tagsGroups`]="{ item }">
      <template v-for="(tagsGroup, groupIndex) in item.tagsGroups">
        <template v-for="t in tagsGroup.slice(0, tagsToShow)">
          <v-chip small :key="`tc${t.id}`" class="ml-1 mb-1">
            {{
              `${groupIndex + 1}. ${$t("tags." + t.category)} | ${
                t.description ? t.description : t.name
              }`
            }}
          </v-chip>
        </template>
      </template>
      <v-chip
        small
        class="ml-1 mb-1"
        v-if="item.tagsGroups.some((t) => t.length > tagsToShow)"
      >
        ...
      </v-chip>
    </template>

    <template v-slot:[`item.modes`]="{ item }">
      <template v-for="m in item.modes">
        <v-chip small :key="`mc${m.id}`" class="ml-1 mb-1">
          {{ m.name }}
        </v-chip>
      </template>
    </template>

    <template v-slot:[`item.testId`]="{ item }">
      <span v-if="!!item.testId">✔</span>
    </template>

    <template v-slot:[`item.version.comments`]="{ item }">
      <instruction-comments
        v-if="Array.isArray(item.version.comments)"
        :buttonTitle="`${item.version.comments.length}`"
        :title="`${item.code} ${item.version.revision}`"
        :versionId="item.version.id"
        :instructionComments="item.version.comments"
        :isButtonText="true"
        :isButtonSmall="false"
        @comment-added="(e) => (item.version.comments = e)"
      />
    </template>

    <template v-slot:[`item.menu`]="{ item }">
      <v-menu bottom left>
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            @click="$emit('addOrEditTest', item.testId, item.id, item.code)"
            >{{
              item.testId
                ? $t("tests.testEdit.edit")
                : $t("tests.testEdit.addTest")
            }}
          </v-list-item>
          <v-list-item
            v-if="item.testId"
            @click="
              $emit('alert', {
                instructionId: item.id,
                testId: item.testId,
                code: item.code,
              })
            "
          >
            {{ $t("tests.testEdit.remove") }}
          </v-list-item>
          <v-list-item
            v-if="!item.isActive"
            @click="$emit('setIsActive', item.id, true)"
          >
            {{ $t("instructions.activate") }}
          </v-list-item>
          <v-list-item
            v-if="item.isActive"
            @click="$emit('setIsActive', item.id, false)"
          >
            {{ $t("instructions.deactivate") }}
          </v-list-item>
          <v-list-item @click="$emit('forceRereading', item)">
            {{ $t("instructions.forceRereading.button") }}
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <template v-slot:no-results>{{
      $t("search.noResults", { item: search })
    }}</template>
  </v-data-table>
</template>

<script>
import InstructionComments from "@/components/instruction/InstructionComments";

import TagsQuery from "@/graphql/Tags.gql";
import InstructionGroupsQuery from "@/graphql/queries/InstructionGroups.gql";

import { getDetailedTagText } from "@/utils/tag";

export default {
  props: ["canEdit", "instructions", "loading", "showActiveCounts"],
  components: {
    InstructionComments,
  },
  data() {
    return {
      search: "",
      filter: false,
      filters: [],
      showActive: true,
      filteredGroups: [],
      sortBy: ["version.comments", "code"],
      sortDesc: [true, true],
    };
  },
  computed: {
    activeInstructionCount() {
      return (
        this.instructions && this.instructions.filter((i) => i.isActive).length
      );
    },
    inactiveInstructionCount() {
      return (
        this.instructions &&
        this.instructions.length - this.activeInstructionCount
      );
    },
    filteredInstructions() {
      if (!this.instructions) return [];
      return this.instructions.filter(
        (i) =>
          i.isActive === this.showActive &&
          (!this.filteredGroups.length ||
            i.groups.some((g) => this.filteredGroups.includes(g.id)))
      );
    },
    tagsToShow() {
      return this.$vuetify.breakpoint.lgAndDown ? 4 : 6;
    },
    headers() {
      const h = [
        {
          text: this.$i18n.t("instructions.column.code"),
          value: "code",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.groups"),
          value: "groups[0].name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.name"),
          value: "version.localizedFile.name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.revision"),
          value: "version.date",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t("instructions.column.dueInDays"),
          value: "version.dueInDays",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t("instructions.column.review"),
          value: "version.rereadingInDays",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t(`instructions.column.tags`),
          value: `tagsGroups`,
          class: "text-uppercase subheading text-truncate",
          filter: (items) => this.filterTagsGroups(items),
        },
        {
          text: this.$i18n.t("instructions.column.modes"),
          value: "modes",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t(`instructions.comments.title`),
          value: "version.comments",
          class: "text-uppercase subheading text-truncate",
          align: "center",
        },
        {
          text: this.$i18n.t(`tests.testEdit.title`),
          value: "testId",
          class: "text-uppercase subheading",
          align: "center",
          width: "80px",
        },
      ];
      if (this.canEdit) {
        h.push({
          text: "",
          value: "menu",
          sortable: false,
          align: "right",
        });
      }
      return h;
    },
  },

  methods: {
    hasGroups(item) {
      return item.groups && item.groups.length > 0;
    },
    setShowActive(value) {
      this.showActive = value;
      this.$emit("showActive", value);
    },
    getDetailedTagText(item) {
      return getDetailedTagText(item);
    },
    filterTagsGroups(tagGroups) {
      if (!this.filter) {
        return true;
      }

      if (!this.filters.length) {
        return tagGroups.every((tagGroup) => tagGroup.length == 0);
      }
      if (tagGroups.length && this.filters.length) {
        return tagGroups.some((tagGroup) =>
          tagGroup.some((tags) =>
            this.filters.map((el) => el.id).includes(tags.id)
          )
        );
      }
      return true;
    },
    getActiveStatusStyle(item) {
      return item.isActive ? "" : "grey--text";
    },

    searchFilter(value, search, item) {
      return (
        !search ||
        search
          .toLowerCase()
          .split(" ")
          .every((w) => item.search.includes(w))
      );
    },
  },
  apollo: {
    tags: {
      query: TagsQuery,
    },
    groups: {
      query: InstructionGroupsQuery,
    },
  },
};
</script>

<style>
.theme--light.v-data-table .v-row-group__header,
.theme--light.v-data-table .v-row-group__summary {
  background: inherit !important;
}
.inactive {
  background: lightgrey !important;
}
</style>
